import React, { useState, useRef, useEffect, useCallback } from "react";
import { useTheme } from "next-themes";
import { useNavigate, useLocation, NavLink } from "react-router-dom";
import { getUserInfo } from "../user";
import { logout } from "../auth";
import config from "../config";
import SecuredImage from "./SecuredImage";
import {
  Clipboard,
  ClipboardList,
  Database,
  DatabaseIcon,
  Home,
  Moon,
  Plus,
  Sun,
  TestTube,
} from "lucide-react";
import DebugPanel from "./DebugPanel";
import gitInfo from "../gitInfo";
import { Button } from "./ui/button";
import { formatDisplayDate } from "../utils/dateFormat";

// Digital Clock Component
function DigitalClock() {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => {
      setTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const formatDate = (date) => {
    // Weekday and DD-MM-YYYY, as dates are shown everywhere
    return `${date.toLocaleDateString('en-GB', { weekday: 'short' })} ${formatDisplayDate(date)}`;
  };

  const formatTime = (date) => {
    return date.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false });
  };

  return (
    <div className="flex flex-col items-center text-center">
      <div className="text-2xl font-bold text-gray-800 dark:text-gray-100 tabular-nums">
        {formatTime(time)}
      </div>
      <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
        {formatDate(time)}
      </div>
    </div>
  );
}

// Custom hook for bearer token management
function useBearerToken() {
  const [token, setToken] = useState(() => {
    if (typeof window !== "undefined") {
      return localStorage.getItem("token") || "";
    }
    return "";
  });
  // Keep token in sync with localStorage
  useEffect(() => {
    function syncToken() {
      setToken(localStorage.getItem("token") || "");
    }
    window.addEventListener("storage", syncToken);
    return () => window.removeEventListener("storage", syncToken);
  }, []);
  // Setter updates both state and localStorage
  const setBearerTokenInternal = (newToken) => {
    setToken(newToken);
    if (typeof window !== "undefined") {
      if (newToken) {
        localStorage.setItem("token", newToken);
      } else {
        localStorage.removeItem("token");
      }
    }
  };
  return [token, setBearerTokenInternal];
}

// Enable debug mode if window.DEBUG_MODE or import.meta.env.VITE_DEBUG_MODE is set
const DEBUG_MODE =
  (typeof window !== "undefined" && window.DEBUG_MODE) ||
  (import.meta.env && import.meta.env.VITE_DEBUG_MODE === "true");

export default function Layout({ children }) {
  const navigate = useNavigate();
  const location = useLocation();
  // Enable debug mode if window.DEBUG_MODE or import.meta.env.VITE_DEBUG_MODE is set
  const DEBUG_MODE =
    (typeof window !== "undefined" && window.DEBUG_MODE) ||
    (import.meta.env && import.meta.env.VITE_DEBUG_MODE === "true");
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [userDropdownOpen, setUserDropdownOpen] = useState(false);
  const [bearerToken] = useBearerToken();
  const userDropdownRef = useRef(null);
  // User info state
  const [user, setUser] = useState(null);
  const [userLoading, setUserLoading] = useState(true);
  // State for collapsible menu
  const masterDataRoutes = [
    "/customers",
    "/hosts",
    "/contracts",
    "/users",
    "/projects",
    "/country",
    "/crops",
    "/study-types",
    "/trialtype",
  ];
  const isMasterDataActive = masterDataRoutes.some((route) =>
    location.pathname.startsWith(route)
  );
  const [masterDataOpen, setMasterDataOpen] = useState(isMasterDataActive);
  useEffect(() => {
    if (isMasterDataActive) setMasterDataOpen(true);
  }, [isMasterDataActive]);

  // Weekly "final report done" reminder (hooks/useTrialNotifications) is disabled for now - it
  // fetched every trial the user has ever had. The hook is kept so it can be switched back on.

  // Theme
  const { theme, setTheme, systemTheme } = useTheme();

  // Fetch user info and check token validity. Only a 401 logs the user out (api.js handles that
  // and redirects); a 5xx, timeout or network error keeps the session and shows a retry banner.
  const [userError, setUserError] = useState(null);
  const mountedRef = useRef(true);
  const loadUser = useCallback(() => {
    setUserLoading(true);
    setUserError(null);
    getUserInfo(false, { throwErrors: true })
      .then((info) => {
        if (!mountedRef.current || !info) return; // no info = 401, already redirecting to /login
        setUser(info);
        setUserLoading(false);
      })
      .catch((err) => {
        if (!mountedRef.current) return;
        const status = err?.response?.status;
        setUserError(status
          ? `Could not load your user info: the server returned an error (${status}).`
          : "Could not load your user info: the server did not respond.");
        setUserLoading(false);
      });
  }, []);
  useEffect(() => {
    mountedRef.current = true;
    loadUser();
    return () => {
      mountedRef.current = false;
    };
  }, [loadUser]);

  // Minimal toast system: listen for 'app:toast' custom events
  const [toasts, setToasts] = useState([]);
  useEffect(() => {
    function onToast(e) {
      const t = e.detail || {};
      const id = Date.now() + Math.random();
      setToasts((ts) => [...ts, { id, ...t }]);
      // auto-dismiss
      setTimeout(() => setToasts((ts) => ts.filter((x) => x.id !== id)), 6000);
    }
    window.addEventListener("app:toast", onToast);
    return () => window.removeEventListener("app:toast", onToast);
  }, []);

  // Close dropdown on outside click or Escape
  useEffect(() => {
    function handleClick(e) {
      if (
        userDropdownRef.current &&
        !userDropdownRef.current.contains(e.target)
      ) {
        setUserDropdownOpen(false);
      }
    }
    function handleEscape(e) {
      if (e.key === "Escape") setUserDropdownOpen(false);
    }
    if (userDropdownOpen) {
      document.addEventListener("mousedown", handleClick);
      document.addEventListener("keydown", handleEscape);
    }
    return () => {
      document.removeEventListener("mousedown", handleClick);
      document.removeEventListener("keydown", handleEscape);
    };
  }, [userDropdownOpen]);

  return (
    <div className="mx-auto flex min-h-dvh w-full min-w-80 flex-col bg-gray-100 lg:pl-72 dark:bg-gray-900 dark:text-gray-100">
      {/* Overlay for mobile sidebar */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 z-40 bg-gray-100/50 p-4 backdrop-blur-xs lg:hidden lg:p-8 dark:bg-gray-900/75"
          onClick={() => setMobileSidebarOpen(false)}
        ></div>
      )}
      {/* Sidebar */}
      <aside
        className={
          `fixed top-0 bottom-0 left-0 z-50 flex w-72 flex-col p-3 transition-transform duration-300 ease-out dark:text-gray-200 ` +
          (mobileSidebarOpen
            ? "translate-x-0 scale-100"
            : "-translate-x-full scale-105") +
          " lg:translate-x-0 lg:scale-100"
        }
        style={{ willChange: "transform" }}
      >
        <div className="flex h-full flex-col rounded-lg border border-gray-200 bg-white shadow-lg shadow-gray-200 dark:border-gray-700/75 dark:bg-gray-800 dark:shadow-gray-950/50">
          {/* Sidebar Header */}
          <div className="flex h-22 flex-none items-center justify-center border-b border-gray-100 px-4 dark:border-gray-700/75">
            <a
              href="/"
              className="group inline-flex items-center gap-2 px-2.5 font-bold tracking-wide text-gray-900 hover:text-gray-600 dark:text-gray-100 dark:hover:text-gray-300 mx-auto"
            >
              <img
                src="/agrolab-logo.png"
                alt="AgroLab Logo"
                className="h-20 w-auto"
              />
            </a>
            {/* Close button for mobile */}
            <button
              className="lg:hidden p-2 ml-2 text-gray-800 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
              onClick={() => setMobileSidebarOpen(false)}
              aria-label="Close sidebar"
              type="button"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 16 16"
                fill="currentColor"
                className="size-4"
              >
                <path d="M5.28 4.22a.75.75 0 0 0-1.06 1.06L6.94 8l-2.72 2.72a.75.75 0 1 0 1.06 1.06L8 9.06l2.72 2.72a.75.75 0 1 0 1.06-1.06L9.06 8l2.72-2.72a.75.75 0 0 0-1.06-1.06L8 6.94 5.28 4.22Z" />
              </svg>
            </button>
          </div>
          {/* Sidebar Content: nav and grow area */}
          <div className="flex grow flex-col overflow-y-auto">
            <nav className="flex flex-col gap-0.5 p-4">
              <button
                type="button"
                onClick={() => navigate("/")}
                className={`group flex items-center gap-2.5 rounded-lg px-2 text-sm font-semibold ${location.pathname === "/"
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                  }`}
              >
                <Home
                  className={`h-5 w-5 ${location.pathname === "/" &&
                    "text-emerald-600 dark:text-emerald-400"
                    }`}
                />
                <span className="grow py-2 text-left">Dashboard</span>
              </button>
              <button
                type="button"
                onClick={() => navigate("/trials")}
                className={`group flex items-center gap-2.5 rounded-lg px-2 text-sm font-semibold ${location.pathname.startsWith("/trials") &&
                    location.pathname !== "/trials/new"
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                  }`}
              >
                {/* Flask/lab icon for trials */}
                <TestTube
                  className={`h-5 w-5 ${location.pathname.startsWith("/trials") &&
                    location.pathname !== "/trials/new" &&
                    "text-emerald-600 dark:text-emerald-400"
                    }`}
                />
                <span className="grow py-2 text-left">Trials</span>
              </button>
              <button
                type="button"
                onClick={() => navigate("/trials/new")}
                className={`group flex items-center gap-2.5 rounded-lg px-2 text-sm font-semibold ${location.pathname === "/trials/new"
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                  }`}
              >
                <Plus
                  className={`h-5 w-5 ${location.pathname === "/trials/new" &&
                    "text-emerald-600 dark:text-emerald-400"
                    }`}
                />
                <span className="grow py-2 text-left">New Trial</span>
              </button>
              <button
                type="button"
                onClick={() => navigate("/reports")}
                className={`group flex items-center gap-2.5 rounded-lg px-2 text-sm font-semibold ${location.pathname.startsWith("/reports")
                    ? "text-emerald-700 dark:text-emerald-400"
                    : "text-gray-800 dark:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                  }`}
              >
                <ClipboardList
                  className={`h-5 w-5 ${location.pathname.startsWith("/reports") && "text-emerald-600 dark:text-emerald-400"}`}
                />
                <span className="grow py-2 text-left">Reports</span>
              </button>

              {/* Example nested menu for admin */}
              {user &&
                Array.isArray(user.roles) &&
                user.roles.includes("admin") && (
                  <div>
                    <button
                      type="button"
                      className={`group flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-sm font-semibold ${isMasterDataActive
                          ? "bg-gray-200/60 dark:bg-gray-700/50 text-emerald-700 dark:text-emerald-400"
                          : "text-gray-800 dark:text-gray-200 hover:bg-gray-200/60 dark:hover:bg-gray-700/50"
                        }`}
                      onClick={() => setMasterDataOpen((v) => !v)}
                      aria-expanded={masterDataOpen}
                    >
                      <DatabaseIcon className={`h-5 w-5 ${isMasterDataActive ? "text-emerald-600 dark:text-emerald-400" : ""}`} />
                      {/* <svg
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                        className={`hi-mini hi-database inline-block size-5 flex-none ${
                          isMasterDataActive
                            ? "text-emerald-600 dark:text-emerald-400"
                            : "text-white dark:text-white"
                        }`}
                      >
                        <path
                          fillRule="evenodd"
                          d="M10 2C5.58 2 2 3.79 2 6v8c0 2.21 3.58 4 8 4s8-1.79 8-4V6c0-2.21-3.58-4-8-4Zm0 2c4.08 0 6 1.53 6 2s-1.92 2-6 2-6-1.53-6-2 1.92-2 6-2Zm6 4.28V14c0 .47-1.92 2-6 2s-6-1.53-6-2V8.28C5.09 9.13 7.36 9.5 10 9.5s4.91-.37 6-1.22Z"
                          clipRule="evenodd"
                        />
                      </svg> */}
                      <span className="grow text-left">Master data</span>
                      <svg
                        className={`ml-auto size-4 transition-transform ${masterDataOpen ? "" : "rotate-180"
                          }`}
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                    {masterDataOpen && (
                      <div className="ml-6 flex flex-col gap-0.5 border-l border-gray-200 dark:border-gray-700/50 pl-2">
                        <button
                          type="button"
                          onClick={() => navigate("/contracts")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/contracts")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Contracts
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/country")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/country")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Country
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/crops")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/crops")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Crops
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/customers")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/customers")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Customers
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/hosts")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/hosts")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Hosts
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/projects")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/projects")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Projects
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/study-types")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/study-types")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Study Types
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/trialtype")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/trialtype")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Trial Type
                        </button>
                        <button
                          type="button"
                          onClick={() => navigate("/users")}
                          className={`group flex items-center gap-2.5 rounded-lg px-2 py-1.5 text-sm font-medium ${location.pathname.startsWith("/users")
                              ? "bg-emerald-100 text-emerald-800 dark:bg-emerald-900 dark:text-emerald-200"
                              : "text-gray-700 hover:bg-gray-200/60 dark:text-gray-300 dark:hover:bg-gray-700/50"
                            }`}
                        >
                          Users
                        </button>
                      </div>
                    )}
                  </div>
                )}
            </nav>
          </div>
          {/* Digital Clock - above user menu */}
          <div className="flex flex-none items-center justify-center border-t border-gray-100 px-4 py-3 dark:border-gray-700/75">
            <DigitalClock />
          </div>
          {/* Sidebar Footer: User Dropdown always at the bottom */}
          <div className="flex flex-none items-center border-t border-gray-100 p-4 dark:border-gray-700/75">
            <div className="relative w-full" ref={userDropdownRef}>
              <button
                type="button"
                className="group relative inline-flex w-full items-center justify-between gap-2 rounded-lg px-2.5 py-1.5 text-sm leading-5 text-gray-800 hover:border-gray-300 hover:text-gray-900 dark:text-gray-200 dark:hover:text-white"
                aria-haspopup="true"
                aria-expanded={userDropdownOpen}
                onClick={() => setUserDropdownOpen((v) => !v)}
                id="user-dropdown-toggle"
                disabled={userLoading || !user}
              >
                <span className="absolute inset-0 scale-75 rounded-lg bg-gray-200/75 opacity-0 transition ease-out group-hover:scale-100 group-hover:opacity-100 group-active:scale-105 group-active:bg-gray-200 dark:bg-gray-700/50 dark:group-active:bg-gray-700/75"></span>
                <span className="relative inline-flex items-center gap-2">
                  <span className="relative inline-block flex-none">
                    <span className="absolute top-0 right-0 -mt-1 -mr-1 size-3 rounded-full border-2 border-white bg-emerald-500"></span>
                    {user && user.avatar ? (
                      <SecuredImage
                        src={user.avatar}
                        alt="User Avatar"
                        className="inline-block size-8 rounded-lg"
                        fallback={
                          <div className="inline-flex size-8 items-center justify-center rounded-lg bg-emerald-700 text-xs font-bold text-white">
                            {user?.initials || "?"}
                          </div>
                        }
                      />
                    ) : (
                      <div className="inline-flex size-8 items-center justify-center rounded-lg bg-emerald-700 text-xs font-bold text-white">
                        {user?.initials || "?"}
                      </div>
                    )}
                  </span>
                  <span className="flex grow flex-col text-left">
                    <span className="w-36 truncate text-sm font-semibold">
                      {/* Show current user's name */}
                      {userLoading
                        ? "Loading..."
                        : user?.name || user?.email || "Unknown"}
                    </span>
                    <span className="w-36 truncate text-xs font-medium text-gray-500 dark:text-gray-400">
                      {/* Show current user's email */}
                      {userLoading ? "" : user?.email || "No mail found"}
                    </span>
                  </span>
                </span>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                  className={`hi-mini hi-chevron-up relative inline-block size-4 flex-none opacity-50 transition-transform ${userDropdownOpen ? "" : "rotate-180"
                    }`}
                >
                  <path
                    fillRule="evenodd"
                    d="M9.47 6.47a.75.75 0 0 1 1.06 0l4.25 4.25a.75.75 0 1 1-1.06 1.06L10 8.06l-3.72 3.72a.75.75 0 0 1-1.06-1.06l4.25-4.25Z"
                    clipRule="evenodd"
                  />
                </svg>
              </button>
              {/* Dropdown menu */}
              {userDropdownOpen && user && (
                <div
                  className="absolute right-0 bottom-full left-0 z-10 mb-1 origin-bottom rounded-lg shadow-xl shadow-gray-500/10 dark:shadow-gray-900"
                  role="menu"
                  aria-labelledby="user-dropdown-toggle"
                >
                  <div className="divide-y divide-gray-100 rounded-lg bg-white ring-1 ring-black/5 dark:divide-gray-700 dark:bg-gray-800 dark:ring-gray-700">
                    <div className="space-y-1 p-2.5">
                      {/* Theme selector */}
                      <div className="px-2.5 pb-1">
                        <div className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400 mb-1">
                          Theme
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            className={`h-8 w-8 ${theme === "light" && "bg-emerald-400 hover:bg-emerald-300"}`}
                            onClick={() => setTheme("light")}
                          >
                            <Sun />
                          </Button>
                          <Button
                            className={`h-8 w-8 ${theme === "dark" && "bg-emerald-400 hover:bg-emerald-300"}`}
                            onClick={() => setTheme("dark")}
                          >
                            <Moon />
                          </Button>
                          <Button className={`h-8  ${theme === "system" && "bg-emerald-400 hover:bg-emerald-300"}`}
                            onClick={() => setTheme("system")}
                          >
                            System
                          </Button>
                        </div>
                      </div>
                      <button
                        type="button"
                        role="menuitem"
                        className="group flex items-center gap-2.5 rounded-lg px-2.5 text-sm font-medium hover:bg-gray-100/60 dark:hover:bg-gray-700/50 w-full text-left"
                        onClick={() => {
                          setUserDropdownOpen(false);
                          navigate("/profile");
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="hi-micro hi-user-circle inline-block size-4 flex-none text-gray-400 group-hover:text-gray-950 dark:group-hover:text-gray-50"
                        >
                          <path
                            fillRule="evenodd"
                            d="M15 8A7 7 0 1 1 1 8a7 7 0 0 1 14 0Zm-5-2a2 2 0 1 1-4 0 2 2 0 0 1 4 0ZM8 9c-1.825 0-3.422.977-4.295 2.437A5.49 5.49 0 0 0 8 13.5a5.49 5.49 0 0 0 4.294-2.063A4.997 4.997 0 0 0 8 9Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="grow py-1.5">Profile</span>
                      </button>
                    </div>
                    <div className="space-y-1 p-2.5">
                      <button
                        type="button"
                        className="group flex w-full items-center gap-2.5 rounded-lg px-2.5 text-left text-sm font-medium hover:bg-gray-100/60 dark:hover:bg-gray-700/50"
                        onClick={() => {
                          logout();
                          sessionStorage.removeItem("userInfo");
                          window.location.reload();
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          viewBox="0 0 16 16"
                          fill="currentColor"
                          className="hi-micro hi-arrow-left-start-on-rectangle inline-block size-4 flex-none text-gray-400 group-hover:text-gray-950 dark:group-hover:text-gray-50"
                        >
                          <path
                            fillRule="evenodd"
                            d="M14 4.75A2.75 2.75 0 0 0 11.25 2h-3A2.75 2.75 0 0 0 5.5 4.75v.5a.75.75 0 0 0 1.5 0v-.5c0-.69.56-1.25 1.25-1.25h3c.69 0 1.25.56 1.25 1.25v6.5c0 .69-.56 1.25-1.25 1.25h-3c-.69 0-1.25-.56-1.25-1.25v-.5a.75.75 0 0 0-1.5 0v.5A2.75 2.75 0 0 0 8.25 14h3A2.75 2.75 0 0 0 14 11.25v-6.5Zm-9.47.47a.75.75 0 0 0-1.06 0L1.22 7.47a.75.75 0 0 0 0 1.06l2.25 2.25a.75.75 0 1 0 1.06-1.06l-.97-.97h7.19a.75.75 0 0 0 0-1.5H3.56l.97-.97a.75.75 0 0 0 0-1.06Z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span className="grow py-1.5">Sign out</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* End Sidebar Footer */}
        </div>
      </aside>
      {/* Header: Only visible on mobile (hidden on lg and up) */}
      <header className="fixed top-0 right-0 left-0 z-30 flex h-16 flex-none items-center bg-gray-100/90 backdrop-blur-xs dark:bg-gray-900/90 lg:hidden">
        <div className="container mx-auto flex w-full justify-between px-4 xl:max-w-7xl">
          <div className="flex flex-none items-center gap-4">
            <a
              href="/"
              className="group inline-flex items-center gap-2 font-bold tracking-wide text-gray-900 hover:text-gray-600 dark:text-gray-100 dark:hover:text-gray-300"
            >
              <img
                src="/agrolab-logo.png"
                alt="AgroLab Logo"
                className="h-8 w-auto"
              />
            </a>
            {/* Sidebar toggle button for mobile */}
            <button
              className="p-2 ml-2 text-gray-800 dark:text-gray-200 hover:text-gray-900 dark:hover:text-white"
              onClick={() => setMobileSidebarOpen(true)}
              aria-label="Open sidebar"
              type="button"
            >
              <svg
                className="hi-solid hi-menu-alt-1 size-5 rotate-180"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h6a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                />
              </svg>
            </button>
          </div>
        </div>
      </header>
      {/* Main Content */}
      <main
        id="page-content"
        className="flex max-w-full flex-auto flex-col pt-3 lg:pt-0"
      >
        <div className="container mx-auto flex w-full grow flex-col p-16 lg:p-3 xl:max-w-8xl">
          {userError && (
            <div role="alert" className="mb-3 flex items-center justify-between gap-3 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-900 dark:bg-red-950/40 dark:text-red-200">
              <span>{userError} You are still logged in.</span>
              <button
                type="button"
                onClick={loadUser}
                className="shrink-0 rounded-md border border-red-300 bg-white px-2 py-1 text-xs font-medium text-red-800 hover:bg-red-100 dark:border-red-800 dark:bg-transparent dark:text-red-200 dark:hover:bg-red-900/40"
              >
                Retry
              </button>
            </div>
          )}
          {children}
        </div>
      </main>

      {/* Debug Panel - floating debug tools */}
      <DebugPanel
        config={config}
        bearerToken={bearerToken}
        user={user}
        gitInfo={gitInfo}
      />
      {/* Toast container */}
      <div
        aria-live="polite"
        className="fixed right-4 top-20 z-50 flex flex-col gap-2"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`max-w-sm rounded-md px-4 py-2 text-sm shadow-lg ${t.type === "error"
                ? "bg-red-600 text-white"
                : "bg-emerald-600 text-white"
              }`}
          >
            {t.message}
          </div>
        ))}
      </div>
    </div>
  );
}
